import React from 'react';
import { useTranslation } from 'react-i18next';
import { Skeleton } from '@mui/material';
import { GridCellParams, GridColDef } from '@mui/x-data-grid';
import { useHisto } from '@/hooks/item';
import { ServiceErrorView } from '@/components/atoms/Error';
import Table from '@/components/molecules/section/Table';
import { Item, ItemHisto } from '@/types/item';
import styles from './Histo.module.scss';

const View: React.FC<{ item: Item }> = ({ item }) => {
  const { t } = useTranslation();
  const data = useHisto(item.productId);

  const columns: GridColDef[] = [
    {
      field: 'date',
      headerName: t('item.histo.date'),
      renderCell: (params: GridCellParams) => {
        return getRowValue(String(params.value));
      },
      minWidth: 200,
    },
    {
      field: 'sellingPrice',
      headerName: t('item.histo.selling_price'),
      renderCell: (params: GridCellParams) => {
        return getRowValue(String(params.value));
      },
    },
    {
      field: 'sellerReceives',
      headerName: t('item.histo.seller_receives'),
      renderCell: (params: GridCellParams) => {
        return getRowValue(String(params.value));
      },
    },
    {
      field: 'sellingPriceEur',
      headerName: t('item.histo.selling_price_eur'),
      renderCell: (params: GridCellParams) => {
        return getRowValue(String(params.value));
      },
    },
    {
      field: 'sellerReceivesEur',
      headerName: t('item.histo.seller_receives_eur'),
      renderCell: (params: GridCellParams) => {
        return getRowValue(String(params.value));
      },
    },
    {
      field: 'action',
      headerName: t('item.histo.action'),
      renderCell: (params: GridCellParams) => {
        return getRowValue(String(params.value));
      },
    },
    {
      field: 'operator',
      headerName: t('item.histo.operator'),
      renderCell: (params: GridCellParams) => {
        return getRowValue(String(params.value));
      },
    },
    {
      field: 'warehouse',
      headerName: t('item.histo.warehouse'),
      renderCell: (params: GridCellParams) => {
        return getRowValue(String(params.value));
      },
    },
    {
      field: 'stockLocation',
      headerName: t('item.histo.stock_location'),
      renderCell: (params: GridCellParams) => {
        return getRowValue(String(params.value));
      },
    },
    {
      field: 'info',
      headerName: t('item.histo.info'),
      renderCell: (params: GridCellParams) => {
        const row: ItemHisto = params.row;
        return getInfo(row.trackingLink, row.info);
      },
    },
  ];

  const rows = data.kind === 'Loaded' ? data.data.data.map((item, index) => ({ ...item, id: index })) : [];

  return (
    <>
      {data.kind === 'Called' && (
        <Skeleton
          height={100}
          variant="rounded"
        />
      )}
      {data.kind === 'Error' && <ServiceErrorView error={data.error} />}
      {data.kind === 'Loaded' && (
        <div
          className={styles.container}
          data-cy="item_histo"
        >
          <Table
            rows={rows}
            columns={columns}
            table={{ kind: 'Table' }}
            autoHeight={false}
          />
        </div>
      )}
    </>
  );
};

function getRowValue(value: string): React.ReactElement {
  return <div className={styles.row}>{value}</div>;
}

function renderValue(value: unknown): React.ReactNode {
  if (typeof value === 'object' && value !== null) {
    return (
      <ul>
        {Object.entries(value).map(([key, val]) => (
          <li key={key}>
            {key}: {renderValue(val)}
          </li>
        ))}
      </ul>
    );
  } else {
    return String(value || '-');
  }
}

function getInfo(trackingLink: string, info: string): React.ReactElement {
  try {
    const anyInfo: Record<string, string> = JSON.parse(String(info));
    if (typeof anyInfo !== 'object') {
      return <>{anyInfo}</>;
    }
    return (
      <div className={styles.info}>
        {Object.keys(anyInfo).map((k) => {
          if (k === 'tracking' && trackingLink && trackingLink !== '') {
            return (
              <div key={k}>
                {k}:
                <a
                  className={styles.link}
                  href={trackingLink}
                  target="_blank"
                  rel="noreferrer"
                >
                  <b>{renderValue(anyInfo[k])}</b>
                </a>
              </div>
            );
          } else {
            return (
              <div key={k}>
                {k}: <b>{renderValue(anyInfo[k])}</b>
              </div>
            );
          }
        })}
      </div>
    );
  } catch {
    return <>{info}</>;
  }
}

export default View;
